import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Pagination,
  effectivePageSize,
  pageSlice,
  type PageSize,
} from "@/components/ui/Pagination";
import { useExecutionModeFromOtherTabs } from "@/hooks/useRememberedExecutionMode";
import { strategiesApi } from "@/lib/api";
import { tradeLogApi } from "@/lib/tradeLogApi";
import {
  getSavedExecutionMode,
  getSavedHorizon,
  saveExecutionMode,
  saveHorizon,
} from "@/lib/tradeLogPrefs";
import type { AlertLogEntry, Horizon, TradeLogEntry, TradeLogFilters } from "@/types/tradeLog";
import { AlertsTable } from "./AlertsTable";
import { FiltersBar } from "./FiltersBar";
import { SummaryStats } from "./SummaryStats";
import { TradesTable } from "./TradesTable";

const STRATEGIES_KEY = ["strategies"] as const;
const TRADE_LOG_KEY = ["trade-log"] as const;
const DEFAULT_PAGE_SIZE: PageSize = 5;

interface Paging {
  page: number;
  setPage: (p: number) => void;
  size: PageSize;
  changeSize: (s: PageSize) => void;
}

/** Per-table page + page size. The page resets to 1 whenever `resetKey`
 * (the filters) changes so the operator doesn't land on page 5 of the
 * "yesterday" set after switching to today, and on size changes. */
function usePaging(resetKey: unknown): Paging {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  useEffect(() => {
    setPage(1);
  }, [resetKey]);
  const changeSize = (s: PageSize): void => {
    setSize(s);
    setPage(1);
  };
  return { page, setPage, size, changeSize };
}

/** The Kite console reports charges as one consolidated figure, so the
 * operator enters the total on the summary card; storage is per trade,
 * so distribute it equally across the closed trades in view. */
function useBrokerageSaver(trades: TradeLogEntry[]): (total: number) => Promise<void> {
  const queryClient = useQueryClient();
  return async (total: number): Promise<void> => {
    const closed = trades.filter((t) => t.exitReason !== "OPEN");
    if (closed.length === 0) return;
    const perTrade = total / closed.length;
    await Promise.all(closed.map((t) => tradeLogApi.updateBrokerage(t.id, perTrade)));
    await queryClient.invalidateQueries({ queryKey: TRADE_LOG_KEY });
  };
}

export function TradeLogPage(): ReactElement {
  // Default view is Trades-only — the operator's main screen; alerts are
  // the audit trail they switch to on demand. Live/Paper opens on the
  // last-picked view (a paper-test phase means tracking Paper daily).
  const [filters, setFiltersState] = useState<TradeLogFilters>({
    range: "today",
    strategyId: "all",
    show: "trades",
    executionMode: getSavedExecutionMode(),
  });
  const setFilters = (next: TradeLogFilters): void => {
    saveExecutionMode(next.executionMode ?? "LIVE");
    setFiltersState(next);
  };
  // setFiltersState, not setFilters — this is catching up to another tab's
  // write, not making one.
  useExecutionModeFromOtherTabs((executionMode) => {
    setFiltersState((prev) => ({ ...prev, executionMode }));
  });
  // Intraday vs Positional is a client-side split (operator, 2026-08-10):
  // the two horizons are different books — mixing overnight baskets into a
  // day's scalps made both unreadable. Alerts have no horizon and are
  // unaffected.
  const [horizon, setHorizonState] = useState<Horizon>(getSavedHorizon());
  const setHorizon = (next: Horizon): void => {
    saveHorizon(next);
    setHorizonState(next);
  };
  const pagingKey = useMemo(() => ({ filters, horizon }), [filters, horizon]);
  const tradesPaging = usePaging(pagingKey);
  const alertsPaging = usePaging(pagingKey);

  const strategies = useQuery({
    queryKey: STRATEGIES_KEY,
    queryFn: () => strategiesApi.list(),
  });
  const log = useQuery({
    queryKey: [...TRADE_LOG_KEY, filters],
    queryFn: () => tradeLogApi.list(filters),
  });

  const trades: TradeLogEntry[] = (log.data?.trades ?? []).filter((t) => t.horizon === horizon);
  const alerts: AlertLogEntry[] = log.data?.alerts ?? [];
  const saveBrokerageTotal = useBrokerageSaver(trades);

  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <PageHeader />
      <FiltersBar
        filters={filters}
        onChange={setFilters}
        strategies={strategies.data ?? []}
        horizon={horizon}
        onHorizonChange={setHorizon}
      />
      <SummaryStats trades={trades} onBrokerageSave={saveBrokerageTotal} />
      {filters.show !== "alerts" && <PagedTrades trades={trades} paging={tradesPaging} />}
      {filters.show !== "trades" && <PagedAlerts alerts={alerts} paging={alertsPaging} />}
    </main>
  );
}

function PagedTrades({
  trades,
  paging,
}: Readonly<{
  trades: TradeLogEntry[];
  paging: Paging;
}>): ReactElement {
  const rows = effectivePageSize(paging.size, trades.length);
  return (
    <>
      <TradesTable trades={pageSlice(trades, paging.page, rows)} />
      <Pagination
        currentPage={paging.page}
        totalItems={trades.length}
        pageSize={rows}
        onPageChange={paging.setPage}
        itemNoun="trades"
        sizeValue={paging.size}
        onSizeChange={paging.changeSize}
      />
    </>
  );
}

function PagedAlerts({
  alerts,
  paging,
}: Readonly<{
  alerts: AlertLogEntry[];
  paging: Paging;
}>): ReactElement {
  const rows = effectivePageSize(paging.size, alerts.length);
  return (
    <>
      <AlertsTable alerts={pageSlice(alerts, paging.page, rows)} />
      <Pagination
        currentPage={paging.page}
        totalItems={alerts.length}
        pageSize={rows}
        onPageChange={paging.setPage}
        itemNoun="alerts"
        sizeValue={paging.size}
        onSizeChange={paging.changeSize}
      />
    </>
  );
}

function PageHeader(): ReactElement {
  return (
    <header className="mb-4">
      <h1 className="mb-1">Trade log</h1>
      <p className="text-sm text-ink-secondary">
        Trades the bot took and webhook alerts received. Filter by date, strategy, and type.
      </p>
    </header>
  );
}
